import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Loader2, AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { format, differenceInDays } from "date-fns";
import {
  matchFunds,
  resolveFundCbam,
  type FeBreakdownRow,
  type FeMappingRow,
  type FeSplitOverride,
  type PortfolioFund,
} from "./feMatching";

import FEAssetBreakdownUploader from "./FEAssetBreakdownUploader";
import { useAuth } from "@/hooks/useAuth";

interface Props {
  side: "p" | "r" | "dist" | "mm";
}

export default function FEFundSectorWeightingPanel({ side }: Props) {
  const qc = useQueryClient();
  const { loading: authLoading, session, staffMember } = useAuth() as any;
  const authReady = !authLoading && Boolean(session);
  const isAdmin = staffMember?.role_level === "admin" || staffMember?.role_level === "superadmin";
  
  const weightsTable =
    side === "r"
      ? "ba_r_cbam_fund_weights"
      : side === "dist"
      ? "ba_dist_cbam_fund_weights"
      : side === "mm"
      ? "ba_mm_cbam_fund_weights"
      : "ba_cbam_fund_weights";

  const { data: funds } = useQuery({
    queryKey: ["fe-portfolio-funds", side],
    enabled: authReady,
    queryFn: async () => {
      const [fundsRes, weightsRes] = await Promise.all([
        supabase.from("ba_cbam_funds").select("id, fund_name, isin, fund_manager, fund_class"),
        supabase
          .from(weightsTable)
          .select("fund_id, portfolio, weight_pct, effective_date")
          .order("effective_date", { ascending: false }),
      ]);
      if (fundsRes.error) throw fundsRes.error;
      if (weightsRes.error) throw weightsRes.error;
      // Keep only the latest weight per (fund_id, portfolio); ignore stale older rows.
      const seenKey = new Set<string>();
      const latest: { fund_id: string; weight_pct: number }[] = [];
      for (const w of (weightsRes.data ?? []) as any[]) {
        const k = `${w.fund_id}::${w.portfolio}`;
        if (seenKey.has(k)) continue;
        seenKey.add(k);
        latest.push({ fund_id: w.fund_id, weight_pct: Number(w.weight_pct) });
      }
      const held = new Set(
        latest.filter((w) => w.weight_pct > 0).map((w) => w.fund_id),
      );
      return (fundsRes.data ?? []).filter((f: any) => held.has(f.id)) as PortfolioFund[];
    },
  });

  const { data: feRows } = useQuery({
    queryKey: ["fe-asset-breakdown-latest"],
    enabled: authReady,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fe_asset_breakdown")
        .select("id, isin, fund_name, as_of_date, breakdown")
        .order("as_of_date", { ascending: false });
      if (error) throw error;
      // Keep latest row per ISIN.
      const latest = new Map<string, FeBreakdownRow>();
      for (const r of data ?? []) {
        const row = r as any;
        if (!latest.has(row.isin)) {
          latest.set(row.isin, { ...row, breakdown: row.breakdown ?? {} });
        }
      }
      return Array.from(latest.values());
    },
  });

  const { data: mapping } = useQuery({
    queryKey: ["fe-sector-mapping"],
    enabled: authReady,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fe_sector_mapping")
        .select("*")
        .order("display_order");
      if (error) throw error;
      return data as FeMappingRow[];
    },
  });

  const { data: overrides } = useQuery({
    queryKey: ["fe-split-overrides"],
    enabled: authReady,
    queryFn: async () => {
      const { data, error } = await supabase.from("fe_fund_split_overrides").select("*");
      if (error) throw error;
      return data as FeSplitOverride[];
    },
  });

  const { data: reviews } = useQuery({
    queryKey: ["ba-fund-sector-reviews"],
    enabled: authReady,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ba_cbam_fund_sector_reviews")
        .select("fund_id, last_reviewed_at");
      if (error) throw error;
      return data as { fund_id: string; last_reviewed_at: string }[];
    },
  });



  const { data: masterSectors } = useQuery({
    queryKey: ["ba-master-sectors"],
    enabled: authReady,
    queryFn: async () => {
      const { data, error } = await supabase.from("ba_master_sectors").select("id, name").order("name");
      if (error) throw error;
      return data as { id: string; name: string }[];
    },
  });

  const { data: cbamMappings } = useQuery({
    queryKey: ["ba-fund-sector-mappings"],
    enabled: authReady,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ba_cbam_fund_sector_mappings")
        .select("fund_id, clarification_number, sector_id, weighting");
      if (error) throw error;
      return (data ?? []) as { fund_id: string; clarification_number: number; sector_id: string; weighting: number }[];
    },
  });

  const saveFeCbamMutation = useMutation({
    mutationFn: async ({
      feRowId,
      isin,
      rows,
      preserve,
    }: {
      feRowId: string;
      isin: string;
      rows: { sector_name: string; pct: number }[];
      // Raw FE-column exposure that the dialog did NOT show (unresolved / [needs split]) — kept so the
      // edit can't silently destroy it. Keyed by the original FE column name, with its raw pct.
      preserve?: Record<string, number>;
    }) => {
      // 1. Ensure a DIRECT fe_sector_mapping exists for every CBAM sector used,
      //    so resolveFundCbam passes the breakdown key through unchanged.
      const existingCols = new Set((mapping ?? []).map((m) => m.fe_column_name));
      let nextOrder =
        (mapping ?? []).reduce((m, x) => Math.max(m, x.display_order ?? 0), 0) + 1;
      const newMappings = rows
        .filter((r) => !existingCols.has(r.sector_name))
        .map((r) => ({
          fe_column_name: r.sector_name,
          cbam_master_sector: r.sector_name,
          mapping_type: "DIRECT" as const,
          children: [] as string[],
          display_order: nextOrder++,
        }));
      if (newMappings.length > 0) {
        const { error: mErr } = await supabase
          .from("fe_sector_mapping")
          .upsert(newMappings, { onConflict: "fe_column_name" });
        if (mErr) throw mErr;
      }

      // 2. Overwrite the FE breakdown — but MERGE, don't replace: keep the unresolved exposure the
      //    dialog couldn't show (it edits resolved CBAM sectors only). Edited CBAM keys win on any
      //    name collision. We do NOT wipe this fund's split overrides here: the resolved columns
      //    become DIRECT (which ignores overrides), and the preserved unresolved columns keep any
      //    partial split work the dialog never touched.
      const breakdown: Record<string, number> = {};
      for (const [feCol, pct] of Object.entries(preserve ?? {})) {
        if (Number(pct) > 0) breakdown[feCol] = Number(pct);
      }
      for (const r of rows) {
        if (r.pct > 0) breakdown[r.sector_name] = r.pct;
      }
      const { error } = await supabase
        .from("fe_asset_breakdown")
        .update({ breakdown })
        .eq("id", feRowId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fe-asset-breakdown-latest"] });
      qc.invalidateQueries({ queryKey: ["fe-sector-mapping"] });
      qc.invalidateQueries({ queryKey: ["fe-split-overrides"] });
      toast.success("CBAM allocation saved");
    },
    onError: (e: Error) => toast.error(e.message),
  });



  const upsertOverride = useMutation({
    mutationFn: async ({
      rows,
      remove = [],
    }: {
      rows: { isin: string; fe_column_name: string; child_sector: string; pct: number }[];
      // Prior override rows the user removed (deleted children / cleared columns) — upsert alone
      // would leave these as stale overrides, so delete them explicitly.
      remove?: { isin: string; fe_column_name: string; child_sector: string }[];
    }) => {
      const { data: userData } = await supabase.auth.getUser();
      // Ensure a fe_sector_mapping row exists for every fe_column_name we're
      // about to reference (FK target). For previously-unmapped columns we
      // create a SPLIT row with the chosen child sectors so the override FK
      // resolves.
      const existingCols = new Set((mapping ?? []).map((m) => m.fe_column_name));
      const colsByName = new Map<string, Set<string>>();
      for (const r of rows) {
        if (!colsByName.has(r.fe_column_name)) colsByName.set(r.fe_column_name, new Set());
        colsByName.get(r.fe_column_name)!.add(r.child_sector);
      }
      const newMappings: {
        fe_column_name: string;
        mapping_type: "SPLIT";
        children: string[];
        display_order: number;
      }[] = [];
      let nextOrder =
        (mapping ?? []).reduce((m, x) => Math.max(m, x.display_order ?? 0), 0) + 1;
      for (const [col, children] of colsByName) {
        if (existingCols.has(col)) continue;
        newMappings.push({
          fe_column_name: col,
          mapping_type: "SPLIT",
          children: Array.from(children),
          display_order: nextOrder++,
        });
      }
      if (newMappings.length > 0) {
        const { error: mErr } = await supabase
          .from("fe_sector_mapping")
          .upsert(newMappings, { onConflict: "fe_column_name" });
        if (mErr) throw mErr;
      }
      if (rows.length > 0) {
        const payload = rows.map((r) => ({ ...r, updated_by: userData.user?.id ?? null }));
        const { error } = await supabase
          .from("fe_fund_split_overrides")
          .upsert(payload, { onConflict: "isin,fe_column_name,child_sector" });
        if (error) throw error;
      }

      // Delete overrides the user removed. Group by (isin, fe_column_name) so each column's dropped
      // children are deleted in one call.
      const remByCol = new Map<string, { isin: string; col: string; children: string[] }>();
      for (const r of remove) {
        const k = `${r.isin}::${r.fe_column_name}`;
        if (!remByCol.has(k)) remByCol.set(k, { isin: r.isin, col: r.fe_column_name, children: [] });
        remByCol.get(k)!.children.push(r.child_sector);
      }
      for (const { isin, col, children } of remByCol.values()) {
        if (children.length === 0) continue;
        const { error: delErr } = await supabase
          .from("fe_fund_split_overrides")
          .delete()
          .eq("isin", isin)
          .eq("fe_column_name", col)
          .in("child_sector", children);
        if (delErr) throw delErr;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fe-split-overrides"] });
      qc.invalidateQueries({ queryKey: ["fe-sector-mapping"] });
      toast.success("Split saved");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const verifyMutation = useMutation({
    mutationFn: async (fund_id: string) => {
      const { data: userData } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("ba_cbam_fund_sector_reviews")
        .upsert(
          {
            fund_id,
            last_reviewed_at: new Date().toISOString(),
            reviewed_by: userData.user?.id,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "fund_id" },
        );
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ba-fund-sector-reviews"] });
      toast.success("Marked as reviewed");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateBreakdownMutation = useMutation({
    mutationFn: async ({ id, breakdown }: { id: string; breakdown: Record<string, number> }) => {
      const { error } = await supabase
        .from("fe_asset_breakdown")
        .update({ breakdown })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fe-asset-breakdown-latest"] });
      toast.success("FE breakdown updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const createBreakdownMutation = useMutation({
    mutationFn: async ({ isin, fund_name, breakdown }: { isin: string; fund_name: string; breakdown: Record<string, number> }) => {
      const { data: userData } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("fe_asset_breakdown")
        .insert({
          isin,
          fund_name,
          as_of_date: new Date().toISOString().slice(0, 10),
          breakdown,
          uploaded_by: userData.user?.id ?? null,
        });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fe-asset-breakdown-latest"] });
      qc.invalidateQueries({ queryKey: ["fe-portfolio-funds", side] });
      toast.success("FE breakdown created");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const mappingByCol = useMemo(
    () => new Map((mapping ?? []).map((m) => [m.fe_column_name, m])),
    [mapping],
  );
  const overridesByIsinCol = useMemo(() => {
    const m = new Map<string, FeSplitOverride[]>();
    for (const o of overrides ?? []) {
      const key = `${o.isin}::${o.fe_column_name}`;
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(o);
    }
    return m;
  }, [overrides]);

  const reviewByFundId = useMemo(
    () => new Map((reviews ?? []).map((r) => [r.fund_id, r.last_reviewed_at])),
    [reviews],
  );

  const matches = useMemo(
    () => matchFunds(funds ?? [], feRows ?? []),
    [funds, feRows],
  );

  if (!funds || !feRows || !mapping || !overrides) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const unmatched = matches.filter((m) => m.matchType === "none");

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Holdings Sector Appropriating ({side.toUpperCase()})</h1>
          <p className="text-sm text-muted-foreground">
            Driven by the latest FE Asset Breakdown upload, converted to CBAM Master Sectors via the
            lookup tab. SPLIT and EXPANDED FE categories require a per-fund split (click "Set split").
          </p>
        </div>
      </div>

      {isAdmin && (
        <FEAssetBreakdownUploader
          onUploaded={() => {
            qc.invalidateQueries({ queryKey: ["fe-asset-breakdown-latest"] });
            qc.invalidateQueries({ queryKey: ["fe-portfolio-funds", side] });
          }}
        />
      )}


      {unmatched.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-4 w-4" />
              {unmatched.length} fund{unmatched.length === 1 ? "" : "s"} with no FE breakdown match
            </CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground space-y-1">
            {unmatched.map((u) => (
              <div key={u.fund.id} className="flex items-center justify-between gap-2">
                <div>
                  <span className="font-medium text-foreground">{u.fund.fund_name}</span>
                  {u.fund.fund_class ? ` ${u.fund.fund_class}` : ""}
                  {u.fund.fund_manager ? ` — ${u.fund.fund_manager}` : ""}
                  {u.fund.isin ? ` — ${u.fund.isin}` : " — no ISIN"}
                </div>
                {isAdmin && u.fund.isin && (
                  <EditBreakdownDialog
                    fe={{ id: "", isin: u.fund.isin, fund_name: u.fund.fund_name, as_of_date: new Date().toISOString().slice(0, 10), breakdown: {} }}
                    mapping={mapping ?? []}
                    triggerLabel="Create FE breakdown"
                    onSave={(breakdown) =>
                      createBreakdownMutation.mutateAsync({ isin: u.fund.isin!, fund_name: u.fund.fund_name, breakdown })
                    }
                  />
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">FE → CBAM Allocation per Fund</CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left p-3 font-medium text-muted-foreground min-w-[260px]">Fund</th>
                <th className="text-left p-3 font-medium text-muted-foreground min-w-[100px]">Match</th>
                <th className="text-left p-3 font-medium text-muted-foreground">CBAM Allocation</th>
                <th className="text-center p-3 font-medium text-muted-foreground min-w-[80px]">Total</th>
                <th className="text-center p-3 font-medium text-muted-foreground min-w-[120px]">Splits Needed</th>
                <th className="text-center p-3 font-medium text-muted-foreground min-w-[100px]">Verify</th>
              </tr>
            </thead>
            <tbody>
              {matches
                .filter((m) => m.fe)
                .map((m, idx) => {
                  const result = resolveFundCbam(m.fe!, mappingByCol, overridesByIsinCol);
                  return (
                    <tr
                      key={m.fund.id}
                      className={cn(
                        "border-b align-top",
                        idx % 2 === 0 ? "bg-background" : "bg-muted/20",
                      )}
                    >
                      <td className="p-3 text-xs">
                        <div className="font-medium flex items-center gap-1">
                          {(() => {
                            const last = reviewByFundId.get(m.fund.id);
                            const days = last ? differenceInDays(new Date(), new Date(last)) : null;
                            const stale = days === null || days > 60;
                            if (!stale) return null;
                            return (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Clock className="h-3 w-3 text-amber-600 cursor-help" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    {last
                                      ? `Last reviewed ${format(new Date(last), "dd/MM/yyyy")} (${days} days ago)`
                                      : "Sector allocation has never been reviewed"}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            );
                          })()}
                          {m.fund.fund_name}
                          {m.fund.fund_class ? <span className="text-muted-foreground"> {m.fund.fund_class}</span> : null}
                        </div>
                        <div className="text-muted-foreground text-[10px]">
                          {m.fund.isin ?? "no ISIN"} · {m.fund.fund_manager ?? ""}
                        </div>
                        {isAdmin && (
                          <div className="mt-1">
                            <EditAllocationDialog
                              fundName={m.fund.fund_name}
                              sectors={(masterSectors ?? []).map((s) => s.name)}
                              current={Object.entries(result.allocation)
                                .filter(([sec]) => !sec.startsWith("[needs split] "))
                                .sort((a, b) => b[1] - a[1])
                                .map(([sector_name, pct]) => ({ sector_name, pct: Number(pct) }))}
                              reservedPct={result.unresolved.reduce((s, u) => s + (Number(u.pct) || 0), 0)}
                              onSave={(rows) =>
                                saveFeCbamMutation.mutateAsync({
                                  feRowId: m.fe!.id,
                                  isin: m.fe!.isin,
                                  rows,
                                  // Keep the unresolved [needs split] exposure the dialog can't edit.
                                  preserve: Object.fromEntries(
                                    result.unresolved
                                      .filter((u) => Number(u.pct) > 0)
                                      .map((u) => [u.feColumn, Number(u.pct)]),
                                  ),
                                })
                              }
                            />
                          </div>
                        )}
                      </td>
                      <td className="p-3">
                        <Badge
                          variant={m.matchType === "isin" ? "outline" : "secondary"}
                          className="text-[10px]"
                        >
                          {m.matchType === "isin"
                            ? "ISIN"
                            : `Fuzzy ${(m.confidence * 100).toFixed(0)}%`}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(result.allocation)
                            .sort((a, b) => b[1] - a[1])
                            .map(([sec, pct]) => {
                              const needs = sec.startsWith("[needs split] ");
                              return (
                                <Badge
                                  key={sec}
                                  variant={needs ? "destructive" : "outline"}
                                  className="text-[10px]"
                                >
                                  {sec}: {pct.toFixed(1)}%
                                </Badge>
                              );
                            })}
                        </div>
                      </td>
                      {(() => {
                        const total = Object.values(result.allocation).reduce((s, v) => s + Number(v || 0), 0);
                        const ok = Math.abs(total - 100) < 0.05;
                        return (
                          <td className={cn("p-3 text-center text-xs font-medium tabular-nums", ok ? "text-foreground" : "text-destructive")}>
                            {total.toFixed(1)}%
                          </td>
                        );
                      })()}
                      <td className="p-3 text-center">
                        {result.unresolved.length > 0 ? (
                          <SplitDialog
                            isin={m.fund.isin ?? m.fe!.isin}
                            fundName={m.fund.fund_name}
                            unresolved={result.unresolved}
                            existing={overrides ?? []}
                            allSectors={(masterSectors ?? []).map((s) => s.name)}
                            mapping={mapping ?? []}
                            onSave={(rows, remove) => upsertOverride.mutateAsync({ rows, remove })}
                          />
                        ) : (
                          <Badge variant="outline" className="text-[10px]">
                            <CheckCircle2 className="h-3 w-3 mr-1 text-green-600" />
                            OK
                          </Badge>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          disabled={verifyMutation.isPending}
                          onClick={() => verifyMutation.mutate(m.fund.id)}
                        >
                          Verify
                        </Button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

export function SplitDialog({
  isin,
  fundName,
  unresolved,
  existing,
  allSectors,
  mapping,
  onSave,
}: {
  isin: string;
  fundName: string;
  unresolved: { feColumn: string; pct: number; parent: string; children: string[] }[];
  existing: FeSplitOverride[];
  allSectors: string[];
  mapping: FeMappingRow[];
  onSave: (
    rows: { isin: string; fe_column_name: string; child_sector: string; pct: number }[],
    remove: { isin: string; fe_column_name: string; child_sector: string }[],
  ) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  // Extra parent FE columns the user has added beyond the fund's unresolved set.
  const [extraParents, setExtraParents] = useState<
    { feColumn: string; pct: number; parent: string; children: string[] }[]
  >([]);
  const [addingParent, setAddingParent] = useState(false);
  const allUnresolved = [...unresolved, ...extraParents];
  // Extra child sectors added by the user per FE column (beyond mapping defaults).
  const [extras, setExtras] = useState<Record<string, string[]>>(() => {
    const init: Record<string, string[]> = {};
    for (const u of unresolved) {
      const existingExtras = existing
        .filter((o) => o.isin === isin && o.fe_column_name === u.feColumn)
        .map((o) => o.child_sector)
        .filter((c) => !u.children.includes(c));
      init[u.feColumn] = Array.from(new Set(existingExtras));
    }
    return init;
  });
  const [values, setValues] = useState<Record<string, Record<string, string>>>(() => {
    const init: Record<string, Record<string, string>> = {};
    for (const u of unresolved) {
      init[u.feColumn] = {};
      const all = [...u.children, ...(existing
        .filter((o) => o.isin === isin && o.fe_column_name === u.feColumn)
        .map((o) => o.child_sector)
        .filter((c) => !u.children.includes(c)))];
      for (const c of all) {
        const ov = existing.find(
          (o) => o.isin === isin && o.fe_column_name === u.feColumn && o.child_sector === c,
        );
        init[u.feColumn][c] = ov ? String(ov.pct) : "";
      }
    }
    return init;
  });
  const [addingFor, setAddingFor] = useState<string | null>(null);

  const availableParents = mapping.filter(
    (m) =>
      (m.mapping_type === "SPLIT" || m.mapping_type === "EXPANDED") &&
      !allUnresolved.some((u) => u.feColumn === m.fe_column_name),
  );

  const childrenFor = (u: { feColumn: string; children: string[] }) =>
    [...u.children, ...(extras[u.feColumn] ?? [])];

  const save = async () => {
    const rows: { isin: string; fe_column_name: string; child_sector: string; pct: number }[] = [];
    for (const u of allUnresolved) {
      let sum = 0;
      const colRows: typeof rows = [];
      for (const c of childrenFor(u)) {
        const v = parseFloat(values[u.feColumn]?.[c] ?? "");
        const pct = isNaN(v) ? 0 : v;
        sum += pct;
        colRows.push({ isin, fe_column_name: u.feColumn, child_sector: c, pct });
      }
      if (Math.abs(sum - 100) > 0.5 && sum !== 0) {
        toast.error(`${u.parent}: splits must sum to 100% (got ${sum.toFixed(1)}%)`);
        return;
      }
      if (sum > 0) rows.push(...colRows);
    }
    // Diff prior overrides against what we're saving: any existing child for a column this dialog
    // manages that is no longer present (removed, or in a column the user zeroed out) must be
    // deleted, not just left behind by the upsert.
    const managedCols = new Set(allUnresolved.map((u) => u.feColumn));
    const keep = new Set(rows.map((r) => `${r.fe_column_name}::${r.child_sector}`));
    const remove = existing
      .filter(
        (o) =>
          o.isin === isin &&
          managedCols.has(o.fe_column_name) &&
          !keep.has(`${o.fe_column_name}::${o.child_sector}`),
      )
      .map((o) => ({ isin: o.isin, fe_column_name: o.fe_column_name, child_sector: o.child_sector }));
    await onSave(rows, remove);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-7 text-xs">
          Set split ({unresolved.length})
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Split allocation — {fundName}</DialogTitle>
          <DialogDescription>
            Distribute each FE parent category across its CBAM child sectors. Add extra sectors if the
            defaults don't cover the fund's exposure. Each row must total 100%.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
          {allUnresolved.map((u) => {
            const isExtraParent = extraParents.some((p) => p.feColumn === u.feColumn);
            const children = childrenFor(u);
            const sum = children.reduce(
              (s, c) => s + (parseFloat(values[u.feColumn]?.[c] ?? "") || 0),
              0,
            );
            const ok = Math.abs(sum - 100) < 0.5 || sum === 0;
            const available = allSectors.filter((s) => !children.includes(s));
            return (
              <div key={u.feColumn} className="border rounded-md p-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="text-sm font-medium flex-1">
                    {u.parent}
                    {isExtraParent && (
                      <span className="text-muted-foreground text-xs ml-2">(added)</span>
                    )}
                  </div>
                  {addingFor === u.feColumn ? (
                    <select
                      autoFocus
                      className="text-xs border rounded h-7 px-2 bg-background"
                      defaultValue=""
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v) {
                          setExtras((prev) => ({
                            ...prev,
                            [u.feColumn]: [...(prev[u.feColumn] ?? []), v],
                          }));
                          setValues((prev) => ({
                            ...prev,
                            [u.feColumn]: { ...(prev[u.feColumn] ?? {}), [v]: "" },
                          }));
                        }
                        setAddingFor(null);
                      }}
                      onBlur={() => setAddingFor(null)}
                    >
                      <option value="" disabled>Pick sector…</option>
                      {available.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={() => setAddingFor(u.feColumn)}
                      disabled={available.length === 0}
                    >
                      + Add sector
                    </Button>
                  )}
                  <Badge variant={ok ? "outline" : "destructive"} className="text-[10px]">
                    Sum {sum.toFixed(1)}%
                  </Badge>
                  {isExtraParent && (
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-destructive text-xs"
                      title="Remove parent category"
                      onClick={() => {
                        setExtraParents((prev) => prev.filter((p) => p.feColumn !== u.feColumn));
                        setExtras((prev) => {
                          const { [u.feColumn]: _omit1, ...rest } = prev;
                          return rest;
                        });
                        setValues((prev) => {
                          const { [u.feColumn]: _omit2, ...rest } = prev;
                          return rest;
                        });
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {children.map((c) => {
                    const isExtra = (extras[u.feColumn] ?? []).includes(c);
                    return (
                      <div key={c} className="flex items-center gap-2">
                        <span className="text-xs flex-1">
                          {c}
                          {isExtra && <span className="text-muted-foreground ml-1">(added)</span>}
                        </span>
                        <Input
                          type="number"
                          step="0.01"
                          className="w-24 h-8 text-xs"
                          value={values[u.feColumn]?.[c] ?? ""}
                          onChange={(e) =>
                            setValues((prev) => ({
                              ...prev,
                              [u.feColumn]: { ...prev[u.feColumn], [c]: e.target.value },
                            }))
                          }
                        />
                        <span className="text-[10px] text-muted-foreground">%</span>
                        {isExtra && (
                          <button
                            type="button"
                            className="text-muted-foreground hover:text-destructive text-xs"
                            title="Remove sector"
                            onClick={() => {
                              setExtras((prev) => ({
                                ...prev,
                                [u.feColumn]: (prev[u.feColumn] ?? []).filter((x) => x !== c),
                              }));
                              setValues((prev) => {
                                const next = { ...(prev[u.feColumn] ?? {}) };
                                delete next[c];
                                return { ...prev, [u.feColumn]: next };
                              });
                            }}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          <div className="pt-1">
            {addingParent ? (
              <select
                autoFocus
                className="text-xs border rounded h-8 px-2 bg-background w-full"
                defaultValue=""
                onChange={(e) => {
                  const col = e.target.value;
                  if (col) {
                    const m = mapping.find((x) => x.fe_column_name === col);
                    if (m) {
                      // Pre-fill from this column's EXISTING overrides — re-adding a parent that is
                      // already fully split must show (and preserve) its split, otherwise saving would
                      // diff it as "removed" and silently delete those overrides.
                      const existingForCol = existing.filter((o) => o.isin === isin && o.fe_column_name === col);
                      const extraChildren = Array.from(
                        new Set(existingForCol.map((o) => o.child_sector).filter((c) => !m.children.includes(c))),
                      );
                      setExtraParents((prev) => [
                        ...prev,
                        {
                          feColumn: col,
                          pct: 0,
                          parent: col.replace(/^Asset %\s*/, ""),
                          children: m.children,
                        },
                      ]);
                      setExtras((prev) => ({ ...prev, [col]: extraChildren }));
                      setValues((prev) => {
                        const init: Record<string, string> = {};
                        for (const c of [...m.children, ...extraChildren]) {
                          const ov = existingForCol.find((o) => o.child_sector === c);
                          init[c] = ov ? String(ov.pct) : "";
                        }
                        return { ...prev, [col]: init };
                      });
                    }
                  }
                  setAddingParent(false);
                }}
                onBlur={() => setAddingParent(false)}
              >
                <option value="" disabled>Pick parent category…</option>
                {availableParents.map((m) => (
                  <option key={m.fe_column_name} value={m.fe_column_name}>
                    {m.fe_column_name.replace(/^Asset %\s*/, "")}
                  </option>
                ))}
              </select>
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs w-full"
                onClick={() => setAddingParent(true)}
                disabled={availableParents.length === 0}
              >
                + Add parent category
              </Button>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={save}>Save splits</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function EditBreakdownDialog({
  fe,
  mapping,
  triggerLabel = "Edit FE",
  onSave,
}: {
  fe: FeBreakdownRow;
  mapping: FeMappingRow[];
  triggerLabel?: string;
  onSave: (breakdown: Record<string, number>) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<{ key: string; value: string }[]>([]);
  const [newCol, setNewCol] = useState("");
  const [saving, setSaving] = useState(false);

  // Initialize on open so re-opens reflect latest data.
  const handleOpenChange = (v: boolean) => {
    if (v) {
      setRows(
        Object.entries(fe.breakdown ?? {}).map(([k, val]) => ({
          key: k,
          value: String(Number(val) || 0),
        })),
      );
      setNewCol("");
    }
    setOpen(v);
  };

  const total = rows.reduce((s, r) => s + (parseFloat(r.value) || 0), 0);
  const ok = Math.abs(total - 100) < 0.5;

  const mappedColumns = Array.from(
    new Set(mapping.map((m) => m.fe_column_name.replace(/^Asset %\s*/, ""))),
  ).sort();

  const usedKeys = new Set(rows.map((r) => r.key));
  const availableMapped = mappedColumns.filter((c) => !usedKeys.has(c));

  const addRow = (key: string) => {
    const trimmed = key.trim();
    if (!trimmed || usedKeys.has(trimmed)) return;
    setRows((prev) => [...prev, { key: trimmed, value: "" }]);
    setNewCol("");
  };

  const save = async () => {
    // Dedupe by key (last wins), drop empty keys and zeros.
    const out: Record<string, number> = {};
    for (const r of rows) {
      const k = r.key.trim();
      if (!k) continue;
      const v = parseFloat(r.value);
      if (isNaN(v) || v === 0) continue;
      out[k] = v;
    }
    setSaving(true);
    try {
      await onSave(out);
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2">
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>FE breakdown — {fe.fund_name}</DialogTitle>
          <DialogDescription>
            Edit the raw FE asset breakdown values for this fund. Should total ~100%. Saves directly to the FE asset breakdown record; will be overwritten on the next FE upload.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
          {rows.length === 0 && (
            <div className="text-xs text-muted-foreground italic py-2">
              No breakdown rows yet — add FE categories below.
            </div>
          )}
          {rows.map((r, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <Input
                className="flex-1 h-8 text-xs"
                value={r.key}
                onChange={(e) =>
                  setRows((prev) =>
                    prev.map((x, i) => (i === idx ? { ...x, key: e.target.value } : x)),
                  )
                }
              />
              <Input
                type="number"
                step="0.01"
                className="w-24 h-8 text-xs"
                value={r.value}
                onChange={(e) =>
                  setRows((prev) =>
                    prev.map((x, i) => (i === idx ? { ...x, value: e.target.value } : x)),
                  )
                }
              />
              <span className="text-[10px] text-muted-foreground">%</span>
              <button
                type="button"
                className="text-muted-foreground hover:text-destructive text-xs px-1"
                title="Remove row"
                onClick={() => setRows((prev) => prev.filter((_, i) => i !== idx))}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 pt-2 border-t">
          <select
            className="text-xs border rounded h-8 px-2 bg-background flex-1"
            value=""
            onChange={(e) => {
              if (e.target.value) addRow(e.target.value);
            }}
          >
            <option value="">Add mapped FE category…</option>
            {availableMapped.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <Input
            className="flex-1 h-8 text-xs"
            placeholder="Or type a custom column name"
            value={newCol}
            onChange={(e) => setNewCol(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addRow(newCol);
              }
            }}
          />
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => addRow(newCol)} disabled={!newCol.trim()}>
            Add
          </Button>
        </div>
        <div className="flex items-center justify-between pt-2">
          <Badge variant={ok ? "outline" : "destructive"} className="text-[10px]">
            Total {total.toFixed(1)}%
          </Badge>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save breakdown"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EditAllocationDialog({
  fundName,
  sectors,
  current,
  reservedPct = 0,
  onSave,
}: {
  fundName: string;
  sectors: string[];
  current: { sector_name: string; pct: number }[];
  // Exposure held outside this dialog (unresolved [needs split] columns), preserved on save. The
  // editable sectors must therefore total (100 - reservedPct), not a flat 100, or the saved
  // breakdown would exceed 100%.
  reservedPct?: number;
  onSave: (rows: { sector_name: string; pct: number }[]) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<{ sector_name: string; value: string }[]>([]);
  const [saving, setSaving] = useState(false);

  const handleOpenChange = (v: boolean) => {
    if (v) {
      setRows(
        current.length > 0
          ? current.map((c) => ({ sector_name: c.sector_name, value: c.pct.toFixed(2) }))
          : [{ sector_name: "", value: "" }],
      );
    }
    setOpen(v);
  };

  // The editable sectors fill whatever isn't held back in unresolved splits.
  const target = Math.max(0, 100 - reservedPct);
  const total = rows.reduce((s, r) => s + (parseFloat(r.value) || 0), 0);
  const ok = Math.abs(total - target) < 0.5;
  const usedNames = new Set(rows.map((r) => r.sector_name).filter(Boolean));

  const addRow = () => {
    if (rows.length >= 5) {
      toast.error("Maximum 5 sectors");
      return;
    }
    setRows((prev) => [...prev, { sector_name: "", value: "" }]);
  };

  const save = async () => {
    const out: { sector_name: string; pct: number }[] = [];
    const seen = new Set<string>();
    for (const r of rows) {
      if (!r.sector_name) continue;
      if (seen.has(r.sector_name)) {
        toast.error("Duplicate sector");
        return;
      }
      const v = parseFloat(r.value);
      if (isNaN(v) || v <= 0) continue;
      seen.add(r.sector_name);
      out.push({ sector_name: r.sector_name, pct: v });
    }
    // Validate the total against the target even when `out` is empty — otherwise clearing every row
    // and saving would drop the resolved exposure entirely (merge-preserve keeps only the unresolved
    // part). An empty save is only valid when there is nothing resolvable to allocate (target ≈ 0).
    if (Math.abs(out.reduce((s, r) => s + r.pct, 0) - target) > 0.5) {
      toast.error(
        reservedPct > 0.05
          ? `Weights must total ~${target.toFixed(1)}% (the remaining ${reservedPct.toFixed(1)}% is held in unresolved splits)`
          : "Weights must total ~100%",
      );
      return;
    }
    setSaving(true);
    try {
      await onSave(out);
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2">
          Edit allocation
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>CBAM allocation — {fundName}</DialogTitle>
          <DialogDescription>
            Edit the FE-derived CBAM allocation (max 5 sectors). Weights must total ~{target.toFixed(0)}%.
            {reservedPct > 0.05 && ` The remaining ${reservedPct.toFixed(1)}% stays in unresolved splits and is preserved.`}
            {" "}Saving overwrites the resolved part of the FE breakdown for this fund.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
          {rows.length === 0 && (
            <div className="text-xs text-muted-foreground italic py-2">
              No sectors yet — add one below.
            </div>
          )}
          {rows.map((r, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <select
                className="flex-1 h-8 text-xs border rounded px-2 bg-background"
                value={r.sector_name}
                onChange={(e) =>
                  setRows((prev) =>
                    prev.map((x, i) => (i === idx ? { ...x, sector_name: e.target.value } : x)),
                  )
                }
              >
                <option value="">Select sector…</option>
                {sectors.map((s) => (
                  <option key={s} value={s} disabled={usedNames.has(s) && s !== r.sector_name}>
                    {s}
                  </option>
                ))}
              </select>
              <Input
                type="number"
                step="0.01"
                className="w-24 h-8 text-xs"
                value={r.value}
                onChange={(e) =>
                  setRows((prev) =>
                    prev.map((x, i) => (i === idx ? { ...x, value: e.target.value } : x)),

                  )
                }
              />
              <span className="text-[10px] text-muted-foreground">%</span>
              <button
                type="button"
                className="text-muted-foreground hover:text-destructive text-xs px-1"
                title="Remove sector"
                onClick={() => setRows((prev) => prev.filter((_, i) => i !== idx))}
              >
                ✕
              </button>
            </div>
          ))}
          <div className="pt-1">
            <Button size="sm" variant="outline" className="h-8 text-xs w-full" onClick={addRow} disabled={rows.length >= 5}>
              + Add sector
            </Button>
          </div>
        </div>
        <div className="flex items-center justify-between pt-2">
          <Badge variant={ok || total === 0 ? "outline" : "destructive"} className="text-[10px]">
            Total {total.toFixed(1)}%{reservedPct > 0.05 ? ` / ${target.toFixed(0)}%` : ""}
          </Badge>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

